import * as Yup from "yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";

import { useRouter, useSearchParams } from "src/routes/hooks";

import { useBoolean } from "src/hooks/use-boolean";

import Iconify from "src/components/iconify";
import FormProvider, {
  RHFCheckbox,
  RHFTextField,
} from "src/components/hook-form";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";

import DialogTitle from "@mui/material/DialogTitle";

import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import { t } from "i18next";
import ProfilesAccordion from "src/components/profiles-accordion/profiles-accordion";
import { playerProfiles, partnerProfiles } from "src/_mock/profiles";
import { Button } from "@mui/material";
import { useLocales } from "src/locales";
// ----------------------------------------------------------------------

type Profile = {
  id: number;
  title: string;
  subtitle: string;
  usageprice: string;
  mainDescription: string;
  subDescription: string;

  imageUrl: string;
};

type AccordionProps = {
  profile: Profile;
  index: number;
};

type Props = {
  registrationStep: number;
  dialog: any;
  handleOnSubmit: (params: any) => void;
};

export default function JwtRegisterView({
  registrationStep,
  dialog,
  handleOnSubmit,
}: Props) {
  const router = useRouter();

  const [errorMsg, setErrorMsg] = useState("");

  const searchParams = useSearchParams();

  const returnTo = searchParams.get("returnTo");

  const password = useBoolean();
  const theme = useTheme();
  const allProfiles = playerProfiles.concat(partnerProfiles);
  const { allLangs, currentLang } = useLocales();
  const RegisterUserSchema = Yup.object().shape({
    firstName: Yup.string().required(t("field_is_required")),
    lastName: Yup.string().required(t("field_is_required")),
    phone: Yup.string()
      .required(t("field_is_required"))
      .min(10, t("phone_number_is_minimum_character"))
      .max(20, t("phone_number_is_maximum_character")),
    password: Yup.string()
      .required(t("new_password_is_required"))
      .min(8, t("password_must_be_text"))
      .test("isValidPass", t("password_must_be_text"), (value, context) => {
        const hasUpperCase = /[A-Z]/.test(value);
        const hasNumber = /[0-9]/.test(value);
        let validConditions = 0;
        const numberOfMustBeValidConditions = 2;
        const conditions = [hasUpperCase, hasNumber];
        conditions.forEach((condition) =>
          condition ? validConditions++ : null
        );
        if (validConditions >= numberOfMustBeValidConditions) {
          return true;
        }
        return false;
      }),
    email: Yup.string()
      .required(t("field_is_required"))
      .email(t("email_must_be_valid")),
    terms: Yup.boolean()
      .oneOf([true], t("field_is_required"))
      .required(t("field_is_required")),
    lang: Yup.string(),
    // not required
    // isPublic: Yup.boolean(),
  });

  const defaultValues = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    terms: false,
    lang: currentLang?.value || "en",

    // isPublic: user?.isPublic || false,
  };

  const methods = useForm({
    resolver: yupResolver(RegisterUserSchema),
    defaultValues,
  });

  const {
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const providers = partnerCheckboxStates
        .filter((item) => item.checked) // Optional: Only include checked items
        .map((item) => item.identifier); // Extract the 'identifier' property
      handleOnSubmit({ ...data, providers });
    } catch (error) {
      console.error(error);
    }
  });

  // Initialize state for checkboxes
  const [playerCheckboxStates, setPlayerCheckboxStates] = useState(
    playerProfiles.map((profile, index) =>
      index !== 0
        ? {
            checked: false,
            disabled: false,
          }
        : { checked: true, disabled: true }
    )
  );

  // Handler to toggle checkbox state
  const handlePlayerCheckboxChange = (index: number) => {
    setPlayerCheckboxStates((prevStates) =>
      prevStates.map((state, i) =>
        i === index ? { ...state, checked: !state.checked } : state
      )
    );
  };

  // Initialize state for checkboxes
  const [partnerCheckboxStates, setPartnerCheckboxStates] = useState(
    partnerProfiles.map((profile, index) => ({
      checked: false,
      disabled: false,
      identifier: profile.identifier,
    }))
  );

  // Handler to toggle checkbox state
  const handlePartnerCheckboxChange = (index: number) => {
    setPartnerCheckboxStates((prevStates) =>
      prevStates.map((state, i) =>
        i === index ? { ...state, checked: !state.checked } : state
      )
    );
  };

  const CloseIcon = () => {
    return (
      <IconButton
        //sx={{ m: 0, color: "black" }}
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          m: 1,
          color: "black",
        }}
        edge="end"
        onClick={dialog.onFalse}
      >
        <Iconify icon="mingcute:close-line" />
      </IconButton>
    );
  };

  const RegStep1 = () => {
    return (
      <>
        <DialogTitle>{t("registration")}</DialogTitle>
        {/* <CloseIcon /> */}
        <DialogContent sx={{ background: "white" }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t("player_profile")} ({t("default")})
          </Typography>
          {playerProfiles.map((playerProfile, index) => (
            <ProfilesAccordion
              key={playerProfile.id}
              profile={playerProfile}
              index={index}
              checkboxStates={playerCheckboxStates}
              handleCheckboxChange={handlePlayerCheckboxChange}
            />
          ))}

          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            {t("partner_profiles")} ({t("optional")})
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {t("partner_registration_helper_text")}
          </Typography>

          {partnerProfiles.map((partnerProfile, index) => (
            <ProfilesAccordion
              key={partnerProfile.id}
              profile={partnerProfile}
              index={index}
              checkboxStates={partnerCheckboxStates}
              handleCheckboxChange={handlePartnerCheckboxChange}
            />
          ))}
        </DialogContent>
      </>
    );
  };

  const RegStep2 = () => {
    return (
      <>
        <DialogTitle>{t("create_player_profile")}</DialogTitle>
        {/* <CloseIcon /> */}
        <DialogContent sx={{ background: theme.palette.grey[100] }}>
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              sx={{ mx: { xs: 2, md: 5 }, my: { xs: 2, md: 7 } }}
            >
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                }}
              >
                <RHFTextField name="firstName" label={t("first_name") + "*"} />
                <RHFTextField name="lastName" label={`${t("last_name")}*`} />

                <RHFTextField name="phone" label={`${t("phone_number")}*`} />
                <RHFTextField
                  name="email"
                  label={`${t("email_address")}*`}
                  onChange={(e) =>
                    setValue("email", e.target.value.toLowerCase())
                  }
                />
              </Box>
              <Box rowGap={3} columnGap={2} display="grid" sx={{ mt: 3 }}>
                <RHFTextField
                  name="password"
                  label={`${t("password")}*`}
                  type={password.value ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={password.onToggle} edge="end">
                          <Iconify
                            icon={
                              password.value
                                ? "solar:eye-bold"
                                : "solar:eye-closed-bold"
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    <Stack component="span" direction="row" alignItems="center">
                      <Iconify
                        icon="material-symbols:info-outline"
                        width={20}
                        sx={{ mr: 1 }}
                      />
                      {t("password_must_be_text")}
                    </Stack>
                  }
                />
              </Box>

              <Box rowGap={3} columnGap={2} display="grid" sx={{ mt: 3 }}>
                <RHFCheckbox name="terms" label={renderTerms} />
              </Box>

              <Box
                justifyContent="flex-end"
                rowGap={3}
                columnGap={2}
                display="grid"
                sx={{ mt: 3 }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("data_with_asterix_is_required")}
                </Typography>
              </Box>

              <Box rowGap={3} columnGap={2} display="grid" sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  {t("registration")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </>
    );
  };

  const renderTerms = (
    <Typography
      component="div"
      variant="body2"
      sx={{
        color: "text.secondary",
      }}
    >
      {t("sign_up_terms_text")}
      <Link
        target="_blank"
        rel="noopener"
        underline="always"
        color="text.primary"
        href="/terms-of-service"
      >
        {t("terms_and_conditions")}
      </Link>
      {", "}
      <Link
        target="_blank"
        rel="noopener"
        underline="always"
        color="text.primary"
        href="/privacy-policy"
      >
        {t("privacy_policy")}
      </Link>
      .
    </Typography>
  );

  const renderForm = (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack>
        {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}

        {registrationStep == 0 ? <RegStep1 /> : <RegStep2 />}
      </Stack>
    </FormProvider>
  );

  return <>{renderForm}</>;
}
